/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useEffect } from 'react'
import { isEmpty } from 'remeda'
import { useLocalStorage } from 'usehooks-ts'
import type { ApolloError } from '@apollo/client'
import type { TransformedProduct } from '@velasca/types'
import { handleError } from '@velasca/utils/handle-error'
import { transformProductsSliderData } from '@velasca/utils/transform-products-slider-data'
import { useSliderProducts } from './useSliderProducts'

interface UseRecentProductsOptions {
  productId?: string
  swapImages?: boolean
  skip?: boolean
}

/**
 * This hook has two main responsibilities:
 * 1. It manage the recent products list in the local storage.
 * 2. It fetches the recent products and transforms them to be used in the slider.
 *
 * In-depth explanation:
 * - The hook maintains a lastVisited state, which is an array of product IDs retrieved from local storage. This state is updated via the setRecentProducts function, which adds a new product ID to the top of the array or moves it to the top if it's already present. The array is limited to ten items.
 * - The useEffect hooks ensure that changes to lastVisited are saved to local storage and that setRecentProducts is called when productId changes.
 * - The hook also fetches products using useSliderProducts and transforms them.
 *
 * @param options - An object containing the product ID, a flag to indicate if the product is being added quickly, and a flag to indicate if the images should be swapped.
 * @returns An object containing the loading state, recent products, and a function to set recent products.
 */
export const useRecentProducts = ({
  productId,
  swapImages,
  skip,
}: UseRecentProductsOptions = {}): {
  loading: boolean
  error: ApolloError | undefined
  recentProducts: TransformedProduct[]
  count: number
  setRecentProducts: (productID: string) => void
} => {
  const [lastVisited, setLastVisited] = useLocalStorage<string[]>(
    'lastVisited',
    [],
  )

  const setRecentProducts = useCallback((productID: string) => {
    if (!productID) {
      return
    }

    setLastVisited((prevLastVisited) => {
      let newLastVisited

      // If product is already there, move it to the top of the array
      if (prevLastVisited.includes(productID)) {
        const filteredLastVisited = prevLastVisited.filter(
          (id) => productID !== id,
        )

        newLastVisited = [productID, ...filteredLastVisited]
      } else {
        newLastVisited = [productID, ...prevLastVisited]
      }
      // If the array has already ten elements, remove the last one
      if (prevLastVisited.length > 10) {
        newLastVisited = newLastVisited.slice(0, -1)
      }

      return newLastVisited
    })
    // TODO: probabilmente si può mettere la dipendenza e togliere il disable
    //eslint-disable-next-line
  }, [])

  const { loading, products, error } = useSliderProducts(lastVisited, skip)

  useEffect(() => {
    if (!productId) {
      return
    }

    setRecentProducts(productId.toString())
  }, [productId, setRecentProducts])

  useEffect(() => {
    if (!error) {
      return
    }

    handleError(error, {
      message: "Couldn't fetch recent products",
      behaviour: 'throw',
    })
  }, [error])

  const transformedProducts = transformProductsSliderData(products, swapImages)

  return {
    loading,
    error,
    recentProducts: transformedProducts,
    count: isEmpty(transformedProducts)
      ? lastVisited.length
      : transformedProducts.length,
    setRecentProducts,
  }
}
